import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const BoasPraticasDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Boas Práticas de Desenvolvimento no SmartEntrega</Title>

      <Paragraph>
        Este documento apresenta as boas práticas de desenvolvimento que devem
        ser seguidas no projeto SmartEntrega para garantir código de qualidade,
        manutenível e escalável.
      </Paragraph>

      <SubTitle>Organização do Código</SubTitle>

      <SubTitle>1. Estrutura de Pastas</SubTitle>
      <Paragraph>
        Mantenha a estrutura de pastas organizada e consistente:
      </Paragraph>
      <List>
        <ListItem>
          <Code>src/components/</Code>: Componentes reutilizáveis
        </ListItem>
        <ListItem>
          <Code>src/pages/</Code>: Componentes de página
        </ListItem>
        <ListItem>
          <Code>src/hooks/</Code>: Hooks personalizados
        </ListItem>
        <ListItem>
          <Code>src/services/</Code>: Serviços e APIs
        </ListItem>
        <ListItem>
          <Code>src/store/</Code>: Configuração do Redux
        </ListItem>
        <ListItem>
          <Code>src/utils/</Code>: Funções utilitárias
        </ListItem>
        <ListItem>
          <Code>src/types/</Code>: Definições de tipos TypeScript
        </ListItem>
        <ListItem>
          <Code>src/styles/</Code>: Estilos globais e temas
        </ListItem>
      </List>

      <SubTitle>2. Nomenclatura</SubTitle>
      <List>
        <ListItem>
          Use <strong>PascalCase</strong> para componentes React:{' '}
          <Code>UserProfile.tsx</Code>
        </ListItem>
        <ListItem>
          Use <strong>camelCase</strong> para arquivos de funções, hooks e
          utilitários: <Code>useAuth.ts</Code>, <Code>formatDate.ts</Code>
        </ListItem>
        <ListItem>
          Use <strong>kebab-case</strong> para arquivos CSS:{' '}
          <Code>button-styles.css</Code>
        </ListItem>
        <ListItem>
          Use nomes descritivos que indicam claramente a função do arquivo
        </ListItem>
      </List>

      <SubTitle>Componentes React</SubTitle>

      <SubTitle>1. Componentes Funcionais</SubTitle>
      <Paragraph>
        Prefira componentes funcionais com hooks em vez de componentes de
        classe:
      </Paragraph>
      <CodeSnippet
        code={`// Bom
const UserProfile: React.FC<UserProfileProps> = ({ user }) => {
  return <div>{user.name}</div>;
};

// Evite
class UserProfile extends React.Component<UserProfileProps> {
  render() {
    return <div>{this.props.user.name}</div>;
  }
}`}
      />

      <SubTitle>2. Props Tipadas</SubTitle>
      <Paragraph>Sempre defina tipos para as props dos componentes:</Paragraph>
      <CodeSnippet
        code={`interface ButtonProps {
  text: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

const Button: React.FC<ButtonProps> = ({ 
  text, 
  onClick, 
  variant = 'primary', 
  disabled = false 
}) => {
  // Implementação...
};`}
      />

      <SubTitle>3. Componentes Pequenos e Focados</SubTitle>
      <Paragraph>
        Mantenha os componentes pequenos e focados em uma única
        responsabilidade:
      </Paragraph>
      <CodeSnippet
        code={`// Ruim: Componente grande com múltiplas responsabilidades
const UserDashboard = () => {
  // Muitas linhas de código, lógica complexa...
};

// Bom: Componentes menores e focados
const UserStats = () => { /* ... */ };
const RecentActivity = () => { /* ... */ };
const UserActions = () => { /* ... */ };

const UserDashboard = () => {
  return (
    <div>
      <UserStats />
      <RecentActivity />
      <UserActions />
    </div>
  );
};`}
      />

      <SubTitle>4. Desestruturação de Props</SubTitle>
      <Paragraph>Use desestruturação para acessar props:</Paragraph>
      <CodeSnippet
        code={`// Ruim
const UserCard = (props) => {
  return (
    <div>
      <h2>{props.user.name}</h2>
      <p>{props.user.email}</p>
    </div>
  );
};

// Bom
const UserCard = ({ user }) => {
  return (
    <div>
      <h2>{user.name}</h2>
      <p>{user.email}</p>
    </div>
  );
};`}
      />

      <SubTitle>5. Evite Props Drilling</SubTitle>
      <Paragraph>
        Evite passar props através de muitos níveis de componentes. Use Context
        API ou Redux para estados globais:
      </Paragraph>
      <CodeSnippet
        code={`// Ruim: Passando props através de muitos componentes
const App = () => {
  const [user, setUser] = useState(null);
  return <Main user={user} setUser={setUser} />;
};

const Main = ({ user, setUser }) => {
  return <Sidebar user={user} setUser={setUser} />;
};

const Sidebar = ({ user, setUser }) => {
  return <UserInfo user={user} setUser={setUser} />;
};

// Bom: Usando Context API
const UserContext = createContext();

const App = () => {
  const [user, setUser] = useState(null);
  return (
    <UserContext.Provider value={{ user, setUser }}>
      <Main />
    </UserContext.Provider>
  );
};

const Main = () => {
  return <Sidebar />;
};

const Sidebar = () => {
  return <UserInfo />;
};

const UserInfo = () => {
  const { user, setUser } = useContext(UserContext);
  // ...
};`}
      />

      <SubTitle>Hooks</SubTitle>

      <SubTitle>1. Regras dos Hooks</SubTitle>
      <Paragraph>Siga as regras dos hooks:</Paragraph>
      <List>
        <ListItem>
          Chame hooks apenas no nível superior (não dentro de condições, loops
          ou funções aninhadas)
        </ListItem>
        <ListItem>
          Chame hooks apenas de componentes funcionais ou hooks personalizados
        </ListItem>
      </List>
      <CodeSnippet
        code={`// Ruim
const Component = () => {
  const [count, setCount] = useState(0);
  
  if (count > 0) {
    // Erro: Hook dentro de condição
    const [name, setName] = useState('');
  }
  
  // ...
};

// Bom
const Component = () => {
  const [count, setCount] = useState(0);
  const [name, setName] = useState('');
  
  if (count > 0) {
    // Lógica condicional sem hooks
  }
  
  // ...
};`}
      />

      <SubTitle>2. Hooks Personalizados</SubTitle>
      <Paragraph>
        Extraia lógica complexa ou reutilizável para hooks personalizados:
      </Paragraph>
      <CodeSnippet
        code={`// src/hooks/useForm.ts
import { useState } from 'react';

export const useForm = <T extends Record<string, any>>(initialValues: T) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    
    // Limpar erro quando o campo é alterado
    if (errors[name as keyof T]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  
  const reset = () => {
    setValues(initialValues);
    setErrors({});
  };
  
  return {
    values,
    errors,
    setValues,
    setErrors,
    handleChange,
    reset
  };
};

// Uso do hook personalizado
const LoginForm = () => {
  const { values, errors, handleChange, setErrors } = useForm({
    email: '',
    password: ''
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validação e lógica de submissão...
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <input
        name="email"
        value={values.email}
        onChange={handleChange}
      />
      {errors.email && <span>{errors.email}</span>}
      
      <input
        type="password"
        name="password"
        value={values.password}
        onChange={handleChange}
      />
      {errors.password && <span>{errors.password}</span>}
      
      <button type="submit">Login</button>
    </form>
  );
};`}
      />

      <SubTitle>TypeScript</SubTitle>

      <SubTitle>1. Tipos Explícitos</SubTitle>
      <Paragraph>
        Defina tipos explícitos para melhorar a legibilidade e a segurança do
        código:
      </Paragraph>
      <CodeSnippet
        code={`// Ruim
const fetchUser = async (id) => {
  const response = await api.get(\`/users/\${id}\`);
  return response.data;
};

// Bom
interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'manager';
}

const fetchUser = async (id: string): Promise<User> => {
  const response = await api.get<User>(\`/users/\${id}\`);
  return response.data;
};`}
      />

      <SubTitle>2. Enums e Union Types</SubTitle>
      <Paragraph>
        Use enums ou union types para valores que têm um conjunto finito de
        opções:
      </Paragraph>
      <CodeSnippet
        code={`// Usando enum
enum UserRole {
  Admin = 'admin',
  User = 'user',
  Manager = 'manager'
}

interface User {
  id: string;
  name: string;
  role: UserRole;
}

// Usando union type
interface User {
  id: string;
  name: string;
  role: 'admin' | 'user' | 'manager';
}`}
      />

      <SubTitle>3. Interfaces vs Types</SubTitle>
      <Paragraph>
        Use interfaces para definir a forma de objetos e types para aliases de
        tipos mais complexos:
      </Paragraph>
      <CodeSnippet
        code={`// Interface para objetos
interface User {
  id: string;
  name: string;
  email: string;
}

// Type para tipos mais complexos
type UserResponse = User | null;
type UserMap = Record<string, User>;
type UserCallback = (user: User) => void;`}
      />

      <SubTitle>Redux</SubTitle>

      <SubTitle>1. Organização de Slices</SubTitle>
      <Paragraph>Organize os slices do Redux por domínio de negócio:</Paragraph>
      <CodeSnippet
        code={`src/store/
├── index.ts
└── slices/
    ├── authSlice.ts
    ├── userSlice.ts
    ├── entregaSlice.ts
    └── uiSlice.ts`}
      />

      <SubTitle>2. Seletores</SubTitle>
      <Paragraph>Use seletores para acessar o estado do Redux:</Paragraph>
      <CodeSnippet
        code={`// src/store/selectors/userSelectors.ts
import { RootState } from '../index';

export const selectAllUsers = (state: RootState) => state.users.users;
export const selectUserById = (state: RootState, userId: string) => 
  state.users.users.find(user => user.id === userId);
export const selectIsLoading = (state: RootState) => state.users.isLoading;

// Uso em componentes
const UserList = () => {
  const users = useAppSelector(selectAllUsers);
  const isLoading = useAppSelector(selectIsLoading);
  
  // ...
};`}
      />

      <SubTitle>3. Thunks para Lógica Assíncrona</SubTitle>
      <Paragraph>
        Use thunks para lógica assíncrona e efeitos colaterais:
      </Paragraph>
      <CodeSnippet
        code={`// src/store/slices/userSlice.ts
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.users.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao buscar usuários');
    }
  }
);

// Uso em componentes
const UserList = () => {
  const dispatch = useAppDispatch();
  
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);
  
  // ...
};`}
      />

      <SubTitle>Estilização</SubTitle>

      <SubTitle>1. Componentes Estilizados</SubTitle>
      <Paragraph>Use Styled Components para estilos encapsulados:</Paragraph>
      <CodeSnippet
        code={`// Componente com estilos encapsulados
const Button = styled.button<{ variant: string }>\`
  padding: 10px 15px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  
  background-color: \${props => {
    switch (props.variant) {
      case 'secondary': return '#6c757d';
      case 'danger': return '#dc3545';
      default: return '#007bff';
    }
  }};
  color: white;
  
  &:hover {
    opacity: 0.9;
  }
\`;`}
      />

      <SubTitle>2. Temas</SubTitle>
      <Paragraph>Use temas para manter consistência visual:</Paragraph>
      <CodeSnippet
        code={`// src/styles/theme.ts
export const theme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    // ...
  },
  // ...
};

// src/App.tsx
const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <AppRoutes />
    </ThemeProvider>
  );
};`}
      />

      <SubTitle>Performance</SubTitle>

      <SubTitle>1. Memoização</SubTitle>
      <Paragraph>
        Use <Code>useMemo</Code> e <Code>useCallback</Code> para evitar
        recálculos desnecessários:
      </Paragraph>
      <CodeSnippet
        code={`// Memoizando valor calculado
const sortedUsers = useMemo(() => {
  return [...users].sort((a, b) => a.name.localeCompare(b.name));
}, [users]);

// Memoizando função de callback
const handleUserClick = useCallback((userId: string) => {
  dispatch(selectUser(userId));
}, [dispatch]);`}
      />

      <SubTitle>2. React.memo</SubTitle>
      <Paragraph>
        Use <Code>React.memo</Code> para evitar renderizações desnecessárias de
        componentes:
      </Paragraph>
      <CodeSnippet
        code={`const UserItem = React.memo(({ user, onSelect }: UserItemProps) => {
  return (
    <div onClick={() => onSelect(user.id)}>
      {user.name}
    </div>
  );
});`}
      />

      <SubTitle>3. Virtualização para Listas Grandes</SubTitle>
      <Paragraph>
        Use bibliotecas de virtualização como <Code>react-window</Code> ou{' '}
        <Code>react-virtualized</Code> para listas grandes:
      </Paragraph>
      <CodeSnippet
        code={`import { FixedSizeList } from 'react-window';

const UserList = ({ users }) => {
  const Row = ({ index, style }) => (
    <div style={style}>
      {users[index].name}
    </div>
  );

  return (
    <FixedSizeList
      height={500}
      width="100%"
      itemCount={users.length}
      itemSize={50}
    >
      {Row}
    </FixedSizeList>
  );
};`}
      />

      <SubTitle>Testes</SubTitle>

      <SubTitle>1. Testes Unitários</SubTitle>
      <Paragraph>
        Escreva testes unitários para componentes e funções:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/__tests__/Button.test.tsx
import { render, screen, fireEvent } from '@testing-library/react';
import Button from '../Button';

describe('Button', () => {
  test('renders with correct text', () => {
    render(<Button text="Click me" onClick={() => {}} />);
    expect(screen.getByText('Click me')).toBeInTheDocument();
  });
  
  test('calls onClick when clicked', () => {
    const handleClick = jest.fn();
    render(<Button text="Click me" onClick={handleClick} />);
    fireEvent.click(screen.getByText('Click me'));
    expect(handleClick).toHaveBeenCalledTimes(1);
  });
  
  test('is disabled when disabled prop is true', () => {
    render(<Button text="Click me" onClick={() => {}} disabled />);
    expect(screen.getByText('Click me')).toBeDisabled();
  });
});`}
      />

      <SubTitle>2. Testes de Integração</SubTitle>
      <Paragraph>Escreva testes de integração para fluxos completos:</Paragraph>
      <CodeSnippet
        code={`// src/pages/__tests__/Login.test.tsx
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from '../../store';
import Login from '../Login';
import { login } from '../../store/slices/authSlice';

// Mock do módulo de API
jest.mock('../../services/api', () => ({
  auth: {
    login: jest.fn(),
  },
}));

describe('Login Page', () => {
  test('submits form with correct values', async () => {
    // Mock da action de login
    const mockLoginAction = jest.spyOn(require('../../store/slices/authSlice'), 'login');
    mockLoginAction.mockImplementation(() => ({
      type: 'auth/login/fulfilled',
      payload: { user: { name: 'Test User' } },
    }));
    
    render(
      <Provider store={store}>
        <BrowserRouter>
          <Login />
        </BrowserRouter>
      </Provider>
    );
    
    // Preencher o formulário
    fireEvent.change(screen.getByLabelText(/empresa/i), {
      target: { value: 'empresa-teste' },
    });
    fireEvent.change(screen.getByLabelText(/email/i), {
      target: { value: 'usuario@teste.com' },
    });
    fireEvent.change(screen.getByLabelText(/senha/i), {
      target: { value: 'senha123' },
    });
    
    // Enviar o formulário
    fireEvent.click(screen.getByRole('button', { name: /entrar/i }));

    // Verificar se a action foi chamada com os valores corretos
    await waitFor(() => {
      expect(mockLoginAction).toHaveBeenCalledWith({
        empresa: 'empresa-teste',
        email: 'usuario@teste.com',
        password: 'senha123',
      });
    });
  });
});`}
      />
    </DocContainer>
  );
};

export default BoasPraticasDoc;
